import React from 'react';
import PropTypes from 'prop-types';
import ModalContext from './ModalContext';
import AngularModalTrigger from './AngularModalTrigger';

/**
 * @deprecated
 * Provides an interface to render and open Modals.
 *
 * @example Modal opened immediately on mount
 *   <ModalTrigger
 *     renderModal={(close) => (
 *       <Modal onCloseRequest={close}>
 *         <Text>Example content</Text>
 *       </Modal>
 *     )}
 *   />
 *
 * @example Modal opened on button click
 *   <ModalTrigger
 *     renderModal={(close) => (
 *       <Modal onCloseRequest={close}>
 *         <Text>Example content</Text>
 *       </Modal>
 *     )}
 *     renderTrigger={(open) => (
 *       <Button text="Open" onClick={open} />
 *     )}
 *   />
 *
 * @example Modal opened initially and on button click
 *   <ModalTrigger
 *     renderModal={(close) => (
 *       <Modal onCloseRequest={close}>
 *         <Text>Example content</Text>
 *       </Modal>
 *     )}
 *     renderTrigger={(open) => (
 *       <Button text="Open" onClick={open} />
 *     )}
 *     initialOpen
 *   />
 *
 * @example Modal with response
 *   <ModalTrigger
 *     renderModal={(close) => (
 *       <ConfirmModal ok={() => close(true)} cancel={() => close(false)}>
 *         <Text>Example question?</Text>
 *       </ConfirmModal>
 *     )}
 *     renderTrigger={(open) => (
 *       <Button text="Open" onClick={open} />
 *     )}
 *     onModalClose={(confirmed) => (confirmed
 *       ? handleOk()
 *       : handleCancel()
*      )}
 *   />
 *
 * See also:
 *   Modal
 *   ModalHost
 */
export default class ModalTrigger extends React.Component {
  static propTypes = {
    /** Renders the Modal when the trigger calls `open`.
        Example: renderModal={(close) => <Modal close={close}>...</Modal>} */
    renderModal: PropTypes.func.isRequired,
    /** Render the content that will open the modal. Default is to open immediately.
        Example: renderTrigger={(open) => <Button onClick={open}>...</Button>} */
    renderTrigger: PropTypes.func,
    /** Called when open is called by a child of renderTrigger */
    onTriggerClick: PropTypes.func,
    /** Called when the modal is opened */
    onModalOpen: PropTypes.func,
    /** Called when the modal is hidden. This gets passed the result passed into `close`.
        Example: onModalClose={(success) => success && onSucceeded()} */
    onModalClose: PropTypes.func,
    /** Overrides default behavior to allow immediately opening a modal that has a trigger */
    initialOpen: PropTypes.bool,
  };

  static defaultProps = {
    renderTrigger: undefined,
    onTriggerClick: undefined,
    onModalOpen: undefined,
    onModalClose: undefined,
    initialOpen: false,
  };

  static contextType = ModalContext;

  constructor(props) {
    super(props);
    this.modalId = null;
  }

  componentDidMount() {
    const { renderTrigger, initialOpen } = this.props;

    // Open immediately if no renderTrigger is provided
    if (!renderTrigger || initialOpen) {
      this.handleOpen();
    }
  }

  componentWillUnmount() {
    // Remove modal from host if still open, e.g. a parent component rerendered while open
    this.handleClose();
  }

  handleOpen = () => {
    const { onTriggerClick, onModalOpen, renderModal } = this.props;

    if (onTriggerClick) {
      onTriggerClick();
    }

    if (this.modalId) {
      return;
    }

    if (this.context) {
      const modal = renderModal(this.handleClose);
      this.modalId = this.context.add(modal, this.handleRemove);
    } else {
      console.warn('ModalHost context not found during handleOpen');
    }

    if (onModalOpen) {
      onModalOpen();
    }
  };

  handleClose = result => {
    if (!this.modalId) {
      return;
    }

    if (this.context) {
      this.context.remove(this.modalId, result);
    } else {
      console.warn('ModalHost context not found during handleClose');
    }

    this.handleRemove(result);
  };

  handleRemove = result => {
    const { onModalClose } = this.props;

    this.modalId = null;

    if (onModalClose) {
      onModalClose(result);
    }
  };

  render() {
    const { renderTrigger } = this.props;

    if (!this.context) {
      // Fallback to Angular modal if available
      if (AngularModalTrigger.hasContext()) {
        return (
          <AngularModalTrigger {...this.props} triggerType={ModalTrigger} />
        );
      }
    }

    // Render trigger or nothing if opening immediately
    return renderTrigger
      ? renderTrigger(this.handleOpen)
      : null;
  }
}
